import React from "react";

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { PasswordToggableIconEndAdornment } from "../PasswordToggableIconEndAdornment";

import type { LoginData } from "./types";

export interface Props {
  onLogin: VoidFunction;
  onFormChange: (key: keyof LoginData, value: string) => void;
  onShowPasswordChange: () => void;
  showPassword: boolean;
  loginError: null | string;
  submitLoading: boolean;
  email: string;
  password: string;
  clearError: VoidFunction;
}

export function Login(props: Props) {
  const {
    onLogin,
    onFormChange,
    onShowPasswordChange,
    showPassword,
    loginError,
    submitLoading,
    email,
    password,
    clearError,
  } = props;
  const navigate = useNavigate();

  const loginOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      onLogin();
    }
  };

  const onForgotPasswordClicked = () => {
    clearError();
    navigate("/forgot-password");
  };

  const onCreateAccountClicked = () => {
    clearError();
    navigate("/sign-up");
  };

  return (
    <Box>
      <Typography textAlign="center" variant="h4" fontWeight="bold">
        Login to Evotrux
      </Typography>
      <Stack my="2rem" spacing={2}>
        <TextField
          data-testid="email-input"
          id="email-input"
          name="email"
          autoComplete="email"
          label="Email"
          variant="outlined"
          onChange={(e) => onFormChange("email", e.target.value)}
          onKeyPress={loginOnEnter}
          value={email}
          autoFocus
        />
        <TextField
          data-testid="password-input"
          id="password-input"
          name="password"
          autoComplete="current-password"
          label="Password"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          onChange={(e) => onFormChange("password", e.target.value)}
          InputProps={{
            endAdornment: (
              <PasswordToggableIconEndAdornment
                showPassword={showPassword}
                onIconClick={onShowPasswordChange}
              />
            ),
          }}
          onKeyPress={loginOnEnter}
          value={password}
          className="sentry-block"
        />
        {loginError !== null && (
          <Typography color="red" align="center">
            {loginError}
          </Typography>
        )}
        {submitLoading ? (
          <Stack alignItems="center">
            <CircularProgress />
          </Stack>
        ) : (
          <Button variant="contained" onClick={onLogin}>
            Login
          </Button>
        )}
        <Divider variant="middle" />
        <Button variant="text" onClick={onForgotPasswordClicked}>
          Forgot Password?
        </Button>
        <Button onClick={onCreateAccountClicked}>Create account</Button>
      </Stack>
    </Box>
  );
}
